import Link from 'next/link';
import React, { FC, useEffect, useState } from 'react';

import { useDelayUnmount } from '@/src/assets/hooks/useDelayUnmount';
import { ITheme } from '@/src/assets/interfaces/themeInterface';
import { baseUrl } from '@/src/assets/utils/baseUrl';

import Button from '../../UI/button/Button';
import { Title } from '../../UI/title/Title';
import Wrapper from '../../UI/wrapper/Wrapper';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import Layout from '../../layout/Layout';
import { useModal } from '../../providers/modalContext/ModalContext';
import { SupportForm } from '../../supportForm/SupportForm';

import styles from './Support.module.scss';

type TSupport = {
    themesData: Partial<ITheme>[];
};

const Support: FC<TSupport> = ({ themesData }) => {
    const { isModalOpen, handleOpenModal } = useModal();
    const [scrollPosition, setScrollPosition] = useState(0);
    const shouldRenderChild = useDelayUnmount(isModalOpen, 400);

    const modalClassName = isModalOpen ? styles['show'] : styles['hide'];
    const wrapperClassName = isModalOpen ? styles['wrapperShow'] : styles['wrapperHide'];
    const themesTitles = themesData.map((theme) => theme.title || 'Theme name undefined');

    useEffect(() => {
        const scrollWidth = window.innerWidth - document.documentElement.clientWidth;

        if (isModalOpen) {
            setScrollPosition(window.scrollY);
            document.body.style.cssText = `overflow-y: hidden;`;
            document.body.style.paddingRight = `${scrollWidth}px`;
        } else {
            setTimeout(() => {
                document.body.style.cssText = `overflow-y: initial;`;
            }, 400);
        }
    }, [isModalOpen]);

    useEffect(() => {
        return () => {
            document.body.style.cssText = 'overflow-y: initial;';
        };
    }, []);

    const jsonLd = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: 'High Five Themes Support',
        description:
            'Harness the ethereal power of this platform to captivate your audience and unleash the full potential of your content.',
        url: `${baseUrl('support')}`,
        potentialAction: {
            '@type': 'ContactAction',
            contactType: 'customer support',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${baseUrl('support')}`,
                httpMethod: 'POST',
            },
        },
    };

    const heading = 'Support';
    const excerpt = `Discover our comprehensive documentation and enjoy expert support whenever you need it.`;

    return (
        <Layout
            meta={{
                metaTitle: 'Support | High Five Themes',
                metaDescription:
                    'Need help with our Ghost themes? Visit our Support page for helpful resources and documentation.',
                ogTitle: 'Support | High Five Themes',
                ogDescription:
                    'Need help with our Ghost themes? Visit our Support page for helpful resources and documentation.',
            }}
            jsonLD={jsonLd}>
            <Wrapper className={styles.wrapper}>
                <Breadcrumbs />
                <div className={styles.info}>
                    <Title text={heading} size="xl" />
                    <p className={` excerpt body-lg excerpt-wrap ${styles.excerpt} `}>{excerpt}</p>
                </div>
                <ul className={styles.theme__cards}>
                    {themesData.map((item) => {
                        return (
                            <li key={item._id} className={styles.theme__cardWrapper}>
                                <Link
                                    href={item.documentationUrl!}
                                    aria-label={`View ${item.title} Documentation`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.theme__card}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="36"
                                        height="36"
                                        viewBox="0 0 36 36"
                                        fill="none">
                                        <path
                                            d="M30.0459 11.5791L22.1709 3.70406C22.0664 3.59962 21.9423 3.51681 21.8057 3.46035C21.6691 3.40389 21.5228 3.37488 21.375 3.375H7.875C7.27826 3.375 6.70597 3.61205 6.28401 4.03401C5.86205 4.45597 5.625 5.02826 5.625 5.625V30.375C5.625 30.9717 5.86205 31.544 6.28401 31.966C6.70597 32.3879 7.27826 32.625 7.875 32.625H28.125C28.7217 32.625 29.294 32.3879 29.716 31.966C30.1379 31.544 30.375 30.9717 30.375 30.375V12.375C30.3751 12.2272 30.3461 12.0809 30.2897 11.9443C30.2332 11.8077 30.1504 11.6836 30.0459 11.5791ZM21.375 12.375V6.1875L27.5625 12.375H21.375Z"
                                            fill="#CED0D8"
                                        />
                                    </svg>
                                    <div className={styles.card__info}>
                                        <Title
                                            text={item.title!}
                                            size="sm"
                                            className={styles.card__title}
                                        />

                                        <div className={styles.link}>
                                            <span>View Documentation</span>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="18"
                                                height="18"
                                                viewBox="0 0 18 18"
                                                fill="none">
                                                <path
                                                    d="M6.43674 5.19924L12.7123 5.28763M12.7123 5.28763L12.8007 11.5632M12.7123 5.28763L5.28769 12.7123"
                                                    strokeWidth="1.7"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
                <Button
                    className={styles.submitReqBtn}
                    ariaLabel="Submit Support request"
                    color="blue"
                    onClick={() => handleOpenModal()}>
                    Submit Support request
                </Button>
                {shouldRenderChild && (
                    <SupportForm
                        className={modalClassName}
                        isOpened={isModalOpen}
                        themesTitles={themesTitles}
                        wrapperClassName={wrapperClassName}
                    />
                )}
            </Wrapper>
        </Layout>
    );
};

export default Support;
